import { useEffect, useLayoutEffect, useRef } from "react";
import { motionTiming, reducedMotion } from "@/lib/motion";

const useBrowserLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;
const fontsLoaded = () => document.fonts?.status === "loaded"
  && document.fonts.check('400 32px "Libre Caslon Display"')
  && document.fonts.check('400 17px "IBM Plex Sans"');

export const useRouteMotion = (mainRef, location, navigationType) => {
  const previous = useRef(null);
  useBrowserLayoutEffect(() => {
    const isFirst = previous.current === null;
    const samePage = previous.current === location.pathname;
    previous.current = location.pathname;
    const root = mainRef.current;
    if (!root || (samePage && !isFirst) || location.hash || (!isFirst && navigationType === "POP")) return;
    // Progressive enhancement: no hidden baseline, font wait, timeout or late replay.
    if (reducedMotion() || !fontsLoaded() || !Element.prototype.animate) return;
    const timing = motionTiming();
    const groups = [
      ["title", timing.title, 0, 6],
      ["metadata", timing.title, timing.metadataDelay, 6],
      ["body", timing.body, timing.bodyDelay, 4],
    ];
    const animations = groups.flatMap(([role, duration, delay, distance]) =>
      [...root.querySelectorAll(`[data-motion="${role}"]`)].map((element) => {
        const animation = element.animate([
          { opacity: 0, transform: `translateY(${distance}px)` },
          { opacity: 1, transform: "translateY(0px)" },
        ], { duration, delay, easing: timing.ease, fill: "backwards" });
        animation.id = `page-${role}`;
        return animation;
      })
    );
    const cancel = () => animations.forEach((animation) => animation.cancel());
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    query.addEventListener("change", cancel);
    // An intent to act always takes precedence over an entrance, with no queue.
    ["pointerdown", "keydown", "wheel", "touchstart"].forEach((event) => window.addEventListener(event, cancel, { passive: true }));
    return () => {
      cancel();
      query.removeEventListener("change", cancel);
      ["pointerdown", "keydown", "wheel", "touchstart"].forEach((event) => window.removeEventListener(event, cancel));
    };
  }, [location.pathname, location.hash, location.key, navigationType, mainRef]);
};